export const OLLIE_ORDERING_LIVE_SPEECH_SILENCE_MS = 1500

export function getOllieOrderingSpeechRecognition(SpeechRecognitionImpl) {
  if (typeof SpeechRecognitionImpl === 'function') {
    return SpeechRecognitionImpl
  }
  if (typeof window === 'undefined') return null
  return window.SpeechRecognition || window.webkitSpeechRecognition || null
}

export function canUseOllieOrderingLiveSpeech({ SpeechRecognitionImpl } = {}) {
  return (
    typeof getOllieOrderingSpeechRecognition(SpeechRecognitionImpl) ===
    'function'
  )
}

export function canUseOllieOrderingVoice({
  mediaRecorder = typeof window !== 'undefined' ? window.MediaRecorder : null,
  mediaDevices = typeof navigator !== 'undefined'
    ? navigator.mediaDevices
    : null,
} = {}) {
  return (
    typeof mediaRecorder === 'function' &&
    typeof mediaDevices?.getUserMedia === 'function'
  )
}

export function ollieOrderingVoiceFailureMessage(error) {
  const name = String(error?.name || '')
  const message = String(error?.message || '').trim()
  if (
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    /permission|notallowed|denied|blocked/i.test(name) ||
    /permission denied|notallowed|could not start audio/i.test(message)
  ) {
    return 'Microphone is blocked. Type what you need instead.'
  }
  if (
    name === 'NotFoundError' ||
    /no microphone|not found|requested device/i.test(message)
  ) {
    return 'No microphone found. Type what you need instead.'
  }
  return message || 'Ollie could not hear that. Type what you need instead.'
}

export function pickOllieOrderingMimeType(isTypeSupported) {
  const supported =
    isTypeSupported ||
    (typeof window !== 'undefined' && window.MediaRecorder?.isTypeSupported
      ? type => window.MediaRecorder.isTypeSupported(type)
      : () => false)
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  return types.find(type => supported(type)) || ''
}

/**
 * Start a one-shot microphone recording. stop() returns a Blob for transcribe.
 */
export async function createOllieOrderingRecorder({
  getUserMedia,
  MediaRecorderImpl,
} = {}) {
  const requestAudio =
    getUserMedia ||
    (typeof navigator !== 'undefined' &&
      navigator.mediaDevices?.getUserMedia?.bind(navigator.mediaDevices))
  const Recorder =
    MediaRecorderImpl ||
    (typeof window !== 'undefined' ? window.MediaRecorder : null)

  if (typeof requestAudio !== 'function' || typeof Recorder !== 'function') {
    throw new Error('Voice is not available in this browser. Type instead.')
  }

  let stream
  try {
    stream = await requestAudio({ audio: true })
  } catch (error) {
    throw new Error(ollieOrderingVoiceFailureMessage(error))
  }
  const mimeType = pickOllieOrderingMimeType()
  const recorder = mimeType
    ? new Recorder(stream, { mimeType })
    : new Recorder(stream)
  const chunks = []

  recorder.addEventListener('dataavailable', event => {
    if (event.data && event.data.size) {
      chunks.push(event.data)
    }
  })
  recorder.start()

  return {
    stop() {
      return new Promise((resolve, reject) => {
        const finish = () => {
          stream.getTracks().forEach(track => track.stop())
          resolve(
            new Blob(chunks, {
              type: recorder.mimeType || mimeType || 'audio/webm',
            }),
          )
        }
        recorder.addEventListener('stop', finish, { once: true })
        recorder.addEventListener(
          'error',
          event => {
            stream.getTracks().forEach(track => track.stop())
            reject(event.error || new Error('Recording failed'))
          },
          { once: true },
        )
        recorder.stop()
      })
    },
  }
}

export function transcriptFromOllieOrderingSpeechEvent(event) {
  const results = event?.results
  if (!results || !results.length) {
    return { finals: '', interim: '', text: '' }
  }
  let finals = ''
  let interim = ''
  for (let i = 0; i < results.length; i += 1) {
    const result = results[i]
    const piece = String(result?.[0]?.transcript || '').trim()
    if (!piece) continue
    if (result.isFinal) {
      finals = finals ? `${finals} ${piece}` : piece
    } else {
      interim = interim ? `${interim} ${piece}` : piece
    }
  }
  const text = [finals, interim].filter(Boolean).join(' ')
  return { finals, interim, text }
}

/**
 * Live typewriter speech. Updates as the chef talks, then ends after silence.
 */
export function createOllieOrderingLiveSpeech({
  SpeechRecognitionImpl,
  lang = 'en-GB',
  silenceMs = OLLIE_ORDERING_LIVE_SPEECH_SILENCE_MS,
  onTranscript,
  onEnd,
  onError,
} = {}) {
  const Recognition = getOllieOrderingSpeechRecognition(SpeechRecognitionImpl)
  if (typeof Recognition !== 'function') {
    throw new Error('Voice is not available in this browser. Type instead.')
  }

  const recognition = new Recognition()
  recognition.continuous = true
  recognition.interimResults = true
  recognition.lang = lang

  let stopped = false
  let latestText = ''
  let silenceTimer = null

  const finish = (text = latestText) => {
    if (stopped) return
    stopped = true
    if (silenceTimer) {
      clearTimeout(silenceTimer)
      silenceTimer = null
    }
    try {
      recognition.stop()
    } catch (error) {
      // Already ended.
    }
    onEnd?.(String(text || '').trim())
  }

  const bumpSilence = () => {
    if (stopped) return
    if (silenceTimer) clearTimeout(silenceTimer)
    silenceTimer = setTimeout(() => finish(latestText), silenceMs)
  }

  recognition.onresult = event => {
    const next = transcriptFromOllieOrderingSpeechEvent(event)
    latestText = next.text
    onTranscript?.(next)
    bumpSilence()
  }

  recognition.onerror = event => {
    const error = String(event?.error || '')
    if (error === 'aborted' || error === 'no-speech') {
      finish(latestText)
      return
    }
    stopped = true
    if (silenceTimer) {
      clearTimeout(silenceTimer)
      silenceTimer = null
    }
    const failure =
      error === 'not-allowed'
        ? Object.assign(new Error('Permission denied'), {
            name: 'NotAllowedError',
          })
        : new Error(
            error || 'Ollie could not hear that. Type what you need instead.',
          )
    onError?.(failure)
  }

  recognition.onend = () => {
    finish(latestText)
  }

  recognition.start()

  return {
    stop() {
      finish(latestText)
    },
  }
}
